import React, { useEffect, useRef } from 'react';

type PlayheadControllerProps = {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    totalTime: number; // 总时长（秒）
    tracksContainerSelector?: string; // 轨道容器，用于计算宽度
    playheadSelector?: string; // 播放头元素
    timelineContentSelector?: string; // 时间轴内容容器，用于滚动
};

// 使用浏览器帧同步驱动黑色刻度线（播放头）移动
// 优先使用 requestVideoFrameCallback，降级到 requestAnimationFrame
// 结合CSS过渡和插值平滑，提供更流畅的视觉体验
const PlayheadController: React.FC<PlayheadControllerProps> = ({
    videoRef,
    totalTime,
    tracksContainerSelector = '.tracks-container',
    playheadSelector = '#playhead',
    timelineContentSelector = '.timeline-content',
}) => {
    const rafIdRef = useRef<number | null>(null);
    const rvfcIdRef = useRef<number | null>(null);
    const isScrollingRef = useRef<boolean>(false); // 标记是否正在滚动动画中

    useEffect(() => {
        const video = videoRef.current;
        const playhead = document.querySelector(playheadSelector) as HTMLElement | null;
        const container = document.querySelector(tracksContainerSelector) as HTMLElement | null;
        const timelineContent = document.querySelector(timelineContentSelector) as HTMLElement | null;
        if (!video || !playhead || !container || !timelineContent || !totalTime || totalTime <= 0) return;

        // 监听滚动结束事件，用于检测滚动动画完成
        let scrollTimeout: NodeJS.Timeout | null = null;
        const handleScroll = () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            isScrollingRef.current = true;
            scrollTimeout = setTimeout(() => {
                isScrollingRef.current = false;
            }, 300); // 滚动动画通常持续约 300ms
        };
        timelineContent.addEventListener('scroll', handleScroll);

        const update = () => {
            // 关键：使用 timeline-content 的可视区域宽度（这是实际的可视窗口）
            const viewportWidth = timelineContent.clientWidth; // 时间轴可视区域宽度（固定）
            // 使用 tracks-container 的总宽度来计算播放头绝对位置
            const totalWidth = container.scrollWidth || container.clientWidth;
            
            if (viewportWidth <= 0 || totalWidth <= 0) return;
            
            // 1. 直接使用视频当前时间计算播放头在轨道容器中的绝对位置
            const videoTime = video.currentTime || 0;
            const playheadAbsoluteX = (videoTime / totalTime) * totalWidth;

            // 2. 获取播放头当前位置（从CSS transform中提取，相对于轨道容器）
            let currentPosition = 0;
            const transform = playhead.style.transform;
            if (transform && transform.startsWith('translateX(')) {
                const match = transform.match(/translateX\(([0-9.]+)px\)/);
                if (match && match[1]) {
                    currentPosition = parseFloat(match[1]);
                }
            }

            // 3. 计算播放头相对于可视区域的X坐标
            // 播放头在轨道容器中的绝对位置 - 时间轴的滚动偏移 = 播放头在可视区域中的位置
            const playheadRelativeX = playheadAbsoluteX - timelineContent.scrollLeft;

            // 4. 步进式滚动检测：当播放头完全接触或超出右边界时（X坐标 >= 可视区域宽度）
            // 如果正在滚动动画中，跳过滚动检测，避免重复触发
            if (!isScrollingRef.current && playheadRelativeX >= viewportWidth) {
                // 4.1 时间轴平滑滚动（移动一个可视区域宽度，带动画）
                const currentScrollLeft = timelineContent.scrollLeft;
                const newScrollLeft = currentScrollLeft + viewportWidth;
                // 确保不超过最大滚动距离（使用 timelineContent 的 scrollWidth）
                const maxScrollLeft = Math.max(0, (timelineContent.scrollWidth || totalWidth) - viewportWidth);
                const finalScrollLeft = Math.min(newScrollLeft, maxScrollLeft);
                
                // 只有当滚动位置发生变化时才执行滚动
                if (finalScrollLeft !== currentScrollLeft) {
                    // 使用 scrollTo 实现平滑滚动动画
                    timelineContent.scrollTo({
                        left: finalScrollLeft,
                        behavior: 'smooth'
                    });
                    
                    // 标记开始滚动动画（滚动事件监听器会自动更新这个状态）
                    isScrollingRef.current = true;
                }
            } else if (!isScrollingRef.current && playheadRelativeX < 0) {
                // 处理播放头移出左边界的情况（向后滚动）
                const currentScrollLeft = timelineContent.scrollLeft;
                const newScrollLeft = Math.max(0, currentScrollLeft - viewportWidth);
                
                if (newScrollLeft !== currentScrollLeft) {
                    timelineContent.scrollTo({
                        left: newScrollLeft,
                        behavior: 'smooth'
                    });
                    
                    // 标记开始滚动动画（滚动事件监听器会自动更新这个状态）
                    isScrollingRef.current = true;
                }
            } else {
                // 5. 正常情况：播放头在可视区域内，正常移动
                // 播放头在轨道容器中的目标位置就是绝对位置（不考虑滚动，因为 translateX 是相对于容器的）
                const targetPosition = playheadAbsoluteX;
                
                // 缓动动画：若位置差较大，分帧平滑移动（避免直接跳跃）
                const distance = targetPosition - currentPosition;
                if (Math.abs(distance) < 0.5) {
                    // 位置差很小时，直接跳到目标位置
                    playhead.style.transform = `translateX(${targetPosition.toFixed(2)}px)`;
                } else {
                    // 缓动：每次移动剩余距离的50%（可调整缓动系数）
                    const newPosition = currentPosition + distance * 0.5;
                    playhead.style.transform = `translateX(${newPosition.toFixed(2)}px)`;
                }
            }
        };

        const loopRAF = () => {
            update();
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const start = () => {
            // 优先使用 requestVideoFrameCallback（更贴近媒体帧）
            const anyVideo = video as any;
            if (typeof anyVideo.requestVideoFrameCallback === 'function') {
                const cb = (_now: number, _metadata: any) => {
                    update();
                    rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                };
                rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                return;
            }
            // 降级到 rAF
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const stop = () => {
            if (rvfcIdRef.current != null) {
                const anyVideo2 = video as any;
                if (typeof anyVideo2.cancelVideoFrameCallback === 'function') {
                    anyVideo2.cancelVideoFrameCallback(rvfcIdRef.current);
                }
                rvfcIdRef.current = null;
            }
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };

        const onPlay = () => start();
        const onPause = () => stop();
        const onEnded = () => stop();

        if (!video.paused) start();
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);

        return () => {
            stop();
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
            timelineContent.removeEventListener('scroll', handleScroll);
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
        };
    }, [videoRef, totalTime, tracksContainerSelector, playheadSelector, timelineContentSelector]);

    return null;
};

export default PlayheadController;


